import React, { useEffect, useMemo, useState } from 'react';
import { FormikErrors, useFormik } from 'formik';

import { TwButton, TwButtonType } from '../../../components/TwButtons/TwButtons';
import TwDialog from '../../../components/TwDialog/TwDialog';
import TwField, { TwInputTypes } from '../../../components/TwFields/TwFields';

import './CreateDriver.scss';
import TwAutocomplete, {
  TwAutocompleteOption
} from '../../../components/TwAutocomplete/TwAutocomplete';
import useAuth from '../../../providers/Auth/Auth.provider';
import StringUtils from '../../../utils/stringUtils';

import CreateEquipment from '../../Equipment/CreateEquipment/CreateEquipment';
import Box from '@mui/material/Box';
import {
  Characters,
  useAllowCharacters,
  useUnformatValue
} from '../../../hooks/useAllowCharacters';
import TwTooltip from '../../../components/TwTooltip/TwTooltip';
import TwCheckbox from '../../../components/TwCheckboxes/TwCheckboxes';
import { CarrierResponse } from '../../../redux/api/carriers/carriers.model';
import { useGetCarriersQuery } from '../../../redux/api/carriers/carrierApi';
import {
  CreateDriverData,
  DRIVER_DEFAULT_PAY_RATE_TYPE,
  DRIVER_TYPE,
  Driver
} from '../../../redux/api/driver/driver.model';
import { useCreateDriverMutation } from '../../../redux/api/driver/driverApi';
import useNotifications from '../../../providers/Notifications/Notifications.provider';
import { useSearchEquipmentsQuery } from '../../../redux/api/equipment/equipmentApi';
import { Equipment, EquipmentType } from '../../../redux/api/equipment/equipment.model';
import { logRocketTrack } from '../../../utils/logRocketUtils';
import useFeatureFlags from '../../../providers/FeatureFlags/FeatureFlags.provider';
import TwCompaniesModal from '../../Companies/CompaniesModal/TwCompaniesModal';
import { CompaniesEntities } from '../../Companies/Companies';
import TwDriverPayRateTypeSelector from '../../../components/TwDriverPayRateTypeSelector/TwDriverPayRateTypeSelector';
import {
  useGetConnectionsQuery,
  useGetDriversByConnectionQuery
} from '../../../redux/api/eld/eldApi';
import { skipToken } from '@reduxjs/toolkit/dist/query';

export interface CreateDriverProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (
    newDriver: Driver,
    selectedTruck?: Equipment,
    selectedCarrier?: CarrierResponse,
    selectedTrailer?: Equipment
  ) => void;
  onCarrierCreated?: (newCarrier: CarrierResponse) => void;
  onEquipmentCreated?: (newEquipment: Equipment) => void;
  fromDocManager?: boolean;
}

interface CreateDriverFormValues {
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  companyName?: string;
  defaultTruck?: TwAutocompleteOption | null;
  defaultTrailer?: TwAutocompleteOption | null;
  defaultPayRateType?: TwAutocompleteOption | null;
  payRate?: string;
  type?: TwAutocompleteOption | null;
  carrier?: TwAutocompleteOption | null;
  deductFuelPurchases?: boolean;
  eldConnectionId?: TwAutocompleteOption | null;
  eldData?: TwAutocompleteOption | null;
}

const initialValues: CreateDriverFormValues = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  companyName: '',
  defaultTruck: null,
  defaultTrailer: null,
  defaultPayRateType: null,
  payRate: '',
  type: null,
  carrier: null,
  deductFuelPurchases: false,
  eldConnectionId: null,
  eldData: null
};

const CreateDriver: React.FC<CreateDriverProps> = ({
  open,
  onCarrierCreated,
  onClose,
  onEquipmentCreated,
  onSuccess,
  fromDocManager = false
}): JSX.Element => {
  const {
    data: { userData }
  } = useAuth();

  const { addNotification } = useNotifications();
  const { enableFuelTransactions, enableLiveTracking } = useFeatureFlags();

  const [carriers, setCarriers] = useState<TwAutocompleteOption[]>([]);
  const [createCarrierDialogOpen, setCreateCarrierDialogOpen] = useState(false);
  const [createEquipmentDialogOpen, setCreateEquipmentDialogOpen] = useState(false);
  const [loadCarriers, setLoadCarriers] = useState<CarrierResponse[]>([]);
  const [loadEquipment, setLoadEquipment] = useState<Equipment[]>([]);
  const [trucks, setTrucks] = useState<TwAutocompleteOption[]>([]);
  const [trailers, setTrailers] = useState<TwAutocompleteOption[]>([]);
  const [types, setTypes] = useState<TwAutocompleteOption[]>([]);
  const [createEquipmentType, setCreateEquipmentType] = useState<EquipmentType | undefined>(
    undefined
  );
  const [formExpanded, setFormExpanded] = useState<boolean>(false);
  const [eldFormExpanded, setEldFormExpanded] = useState<boolean>(false);
  const [emailChecked, setEmailChecked] = useState<boolean>(false);
  const [connectionsOptions, setConnectionsOptions] = useState<TwAutocompleteOption[]>([]);
  const [eldDriversOptions, setEldDriversOptions] = useState<TwAutocompleteOption[]>([]);
  const [selectedEldIntegration, setSelectedEldIntegration] = useState<string | null>(null);

  const { data: carriersList = { content: [], total: 0 } } = useGetCarriersQuery({});
  const { data: equipmentList = { equipments: [], total: 0 } } = useSearchEquipmentsQuery({});
  const { data: connections = { content: [], total: 0 } } = useGetConnectionsQuery({});
  const { data: eldDrivers = { content: [], total: 0 }, isError: hasEldDriversError } =
    useGetDriversByConnectionQuery(
      selectedEldIntegration
        ? {
            connectionId: selectedEldIntegration
          }
        : skipToken
    );
  const [createDriver] = useCreateDriverMutation();

  const [selectedDefaultPayRateType, setSelectedDefaultPayRateType] =
    useState<DRIVER_DEFAULT_PAY_RATE_TYPE | null>(null);

  const { values, handleChange, handleSubmit, resetForm, setFieldValue } = useFormik({
    initialValues: initialValues,
    onSubmit: (values: CreateDriverFormValues): void => {
      if (!values.firstName.trim() || !values.lastName.trim()) {
        logRocketTrack('Team Management', {
          action: 'Create driver',
          severity: 'low',
          tenant_id: userData?.tenant?.id,
          company_name: userData?.tenant?.company_name,
          email: userData?.email_address,
          user_first_name: userData?.first_name,
          user_last_name: userData?.last_name
        });
        addNotification({
          message: 'You must fill "First name" and "Last name"',
          variant: 'error',
          position: 'bottomRight',
          timeToShow: 3000
        });
        return;
      }

      submitDriver({
        ...values,
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        email: values.email?.trim()
      });

      setEmailChecked(false);
      resetForm({
        values: initialValues
      });
    }
  });

  const unformatString = useUnformatValue(Characters.number);
  const handleKeyDownCharacters = useAllowCharacters(Characters.phoneNumber);

  useEffect(() => {
    setFormExpanded(false);
    setEldFormExpanded(false);
  }, []);

  useEffect(() => {
    const newTypes: TwAutocompleteOption[] = [
      {
        id: DRIVER_TYPE.CONTRACTOR,
        label: StringUtils.getDriverTypeLabel(DRIVER_TYPE.CONTRACTOR),
        value: DRIVER_TYPE.CONTRACTOR
      },
      {
        id: DRIVER_TYPE.EMPLOYEE,
        label: StringUtils.getDriverTypeLabel(DRIVER_TYPE.EMPLOYEE),
        value: DRIVER_TYPE.EMPLOYEE
      }
    ];

    setTypes(newTypes);
  }, []);

  useEffect(() => {
    if (!open) return;

    (async (): Promise<void> => {
      try {
        const truckEquipment =
          equipmentList &&
          equipmentList.equipments &&
          equipmentList.equipments.filter((t) => t.type === EquipmentType.truck);

        const trailerEquipment =
          equipmentList &&
          equipmentList.equipments &&
          equipmentList.equipments.filter((t) => t.type === EquipmentType.trailer);

        truckEquipment &&
          setTrucks(truckEquipment.map((e) => ({ id: e.id, label: e.name, value: e.id })));
        equipmentList && setLoadEquipment(equipmentList.equipments);
        trailerEquipment &&
          setTrailers(trailerEquipment.map((e) => ({ id: e.id, label: e.name, value: e.id })));
        carriersList &&
          setCarriers(carriersList.content.map((c) => ({ id: c.id, label: c.name, value: c.id })));
        carriersList && setLoadCarriers(carriersList.content as CarrierResponse[]);
      } catch (error) {
        console.error(error);
      }
    })();
  }, [open, JSON.stringify(carriersList.content), JSON.stringify(equipmentList.equipments)]);

  useEffect(() => {
    if (connections.content && connections.content.length > 0) {
      const options = connections.content.map((connection) => ({
        id: connection.id,
        label: connection.name,
        value: connection.id
      }));

      setConnectionsOptions(options);
      return;
    }

    setConnectionsOptions([]);
  }, [JSON.stringify(connections.content)]);

  useEffect(() => {
    if (hasEldDriversError) {
      setEldDriversOptions([]);
      return;
    }

    if (eldDrivers.content && eldDrivers.content.length > 0) {
      const options = eldDrivers.content.map((eldDriver) => ({
        id: eldDriver.eldDriverId,
        label: `${eldDriver.firstName} ${eldDriver.lastName}`,
        value: eldDriver.eldDriverId
      }));

      setEldDriversOptions(options);
      return;
    }

    setEldDriversOptions([]);
  }, [JSON.stringify(eldDrivers), hasEldDriversError]);

  const submitDriver = (values: CreateDriverFormValues): void => {
    const data: CreateDriverData = {
      firstName: values.firstName,
      lastName: values.lastName,
      carrierId: values.carrier?.id,
      companyName: values.companyName,
      defaultPayRateType: values.defaultPayRateType?.id as DRIVER_DEFAULT_PAY_RATE_TYPE,
      defaultTruckId: values.defaultTruck?.id,
      defaultTrailerId: values.defaultTrailer?.id,
      email: values.email,
      payRate: values.payRate,
      phone: unformatString(values.phone ?? ''),
      type: values.type?.id as DRIVER_TYPE,
      createUserAndSendInvite: emailChecked && values.email ? true : false,
      deductFuelPurchaseOnSettlement: !!values.deductFuelPurchases,
      eldConnectionId: values.eldConnectionId?.id,
      eldData: values.eldData
        ? eldDrivers.content.find((driver) => driver.eldDriverId === values.eldData?.id) ||
          undefined
        : undefined
    };

    createDriver(data)
      .unwrap()
      .then((newDriver) => {
        const newTruck = loadEquipment.find((e) => e.id === newDriver.default_truck_id);
        const newTrailer = loadEquipment.find((e) => e.id === newDriver.default_trailer_id);
        const newCarrier = loadCarriers.find((c) => c.id === newDriver.carrier_id);

        onSuccess(newDriver, newTruck, newCarrier, newTrailer);
        onClose();
      })
      .catch((error) => {
        addNotification({
          message: error.data.message ?? 'Error creating driver',
          variant: 'error',
          position: 'bottomRight',
          timeToShow: 3000
        });
      });
  };

  const handleCarrierCreated = (newCarrier: CarrierResponse): void => {
    const newAutocompleteOption: TwAutocompleteOption = {
      id: newCarrier.id,
      label: newCarrier.company?.name || '',
      value: newCarrier
    };
    const newCarriers = [...carriers, newAutocompleteOption];
    const newLoadCarriers = [...loadCarriers, newCarrier];

    setCarriers(newCarriers);
    setLoadCarriers(newLoadCarriers);

    onCarrierCreated && onCarrierCreated(newCarrier);

    setFieldValue('carrier', newAutocompleteOption);
  };

  const handleEquipmentCreated = (newEquipment: Equipment): void => {
    const newAutocompleteOption: TwAutocompleteOption = {
      id: newEquipment.id,
      label: newEquipment.name,
      value: newEquipment
    };
    const newTrucks = [...trucks, newAutocompleteOption];
    const newLoadEquipment = [...loadEquipment, newEquipment];
    setTrucks(newTrucks);
    setLoadEquipment(newLoadEquipment);

    onEquipmentCreated && onEquipmentCreated(newEquipment);

    let fieldValueEquipmentType = '';

    switch (newEquipment.type) {
      case EquipmentType.truck:
        fieldValueEquipmentType = 'defaultTruck';
        break;
      case EquipmentType.trailer:
        fieldValueEquipmentType = 'defaultTrailer';
        break;
      default:
        fieldValueEquipmentType = '';
    }
    setFieldValue(fieldValueEquipmentType, newAutocompleteOption);
  };

  const handleClose = (cb?: any): void => {
    setFormExpanded(false);
    setEldFormExpanded(false);
    cb && cb();
    onClose();
  };

  const addLinkStyle: React.CSSProperties = {
    color: '#3A4DE9',
    fontFamily: 'Poppins, sans-serif',
    fontSize: '14px',
    fontWeight: '400',
    lineHeight: '18px',
    letterSpacing: '0px',
    textAlign: 'left',
    cursor: 'pointer',
    marginRight: '110px'
  };

  const addEquipmentStyle: React.CSSProperties = {
    ...addLinkStyle,
    display: formExpanded ? 'none' : 'block'
  };

  const addEldStyle: React.CSSProperties = {
    ...addLinkStyle,
    display: eldFormExpanded ? 'none' : 'block'
  };

  const showELDForm = useMemo((): boolean => {
    return enableLiveTracking && connections && connections.total > 0;
  }, [enableLiveTracking, connections]);

  const DialogActions = (): JSX.Element[] => {
    return [
      <TwTooltip key="0" title="Add email to send an invite" placement="top-start">
        <TwCheckbox
          name="sendEmail"
          disabled={!values.email}
          label="Send email invite"
          checked={emailChecked}
          onInputChange={(): void => {
            setEmailChecked((prev) => !prev);
          }}
        />
      </TwTooltip>,
      <TwButton
        key="1"
        fullwidth={false}
        type={TwButtonType.secondary}
        label="Cancel"
        action={(): void => {
          handleClose(resetForm({ values: initialValues }));
          setFormExpanded(false);
          setEldFormExpanded(false);
        }}
      />,
      <TwButton
        key="2"
        fullwidth={false}
        type={TwButtonType.primary}
        label="Create"
        action={handleSubmit}
      />
    ];
  };

  return (
    <>
      <Box component="form" onSubmit={handleSubmit}>
        <TwDialog
          open={open}
          onClickClose={(): void => {
            handleClose(resetForm({ values: initialValues }));
            setFormExpanded(false);
            setEldFormExpanded(false);
          }}
          size="sm"
          title="Create driver"
          classNameActions="contact-details__drivercalculator-dialog-actions"
          actions={DialogActions()}
          draggable={true}
          sx={
            fromDocManager
              ? {
                  zIndex: '1300 !important',
                  '& .MuiDialog-root': {
                    zIndex: '1300 !important'
                  }
                }
              : {}
          }
        >
          <div className="create-driver__wrapper tid-create-driver__wrapper">
            <div className="create-driver__form-section">
              <div className="create-driver__inputs-container">
                <div>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <div className="create-driver__two-column">
                      <div
                        className="tid-create-driver__firstname
                        create-driver__two-column-item"
                      >
                        <TwField
                          id="firstName"
                          name="firstName"
                          value={values.firstName}
                          label="First name"
                          inputType={TwInputTypes.text}
                          disabled={false}
                          onInputChange={handleChange}
                          error={false}
                        />
                      </div>

                      <div
                        className="tid-create-driver__lastname
                        create-driver__two-column-item"
                      >
                        <TwField
                          id="lastName"
                          name="lastName"
                          value={values.lastName}
                          label="Last name"
                          inputType={TwInputTypes.text}
                          onInputChange={handleChange}
                          disabled={false}
                          error={false}
                        />
                      </div>
                    </div>

                    <div className="create-driver__two-column">
                      <div
                        className="tid-create-driver__email
                        create-driver__two-column-item"
                      >
                        <TwField
                          id="email"
                          name="email"
                          value={values.email}
                          label="Email (optional)"
                          inputType={TwInputTypes.text}
                          disabled={false}
                          onInputChange={handleChange}
                          error={false}
                        />
                      </div>

                      <div
                        className="tid-create-driver__phone
                        create-driver__two-column-item"
                      >
                        <TwField
                          id="phone"
                          name="phone"
                          value={values.phone}
                          label="Phone number (optional)"
                          placeholder="(___) ___-____"
                          inputType={TwInputTypes.text}
                          disabled={false}
                          onInputChange={handleChange}
                          error={false}
                          onKeyDown={handleKeyDownCharacters}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div style={{ display: formExpanded ? 'block' : 'none' }}>
                  {userData?.tenantSettings && userData.tenantSettings['advanced-driver-fields'] && (
                    <>
                      <label className="create-driver__sub-title">Default equipment</label>

                      <div className="create-driver__two-column">
                        <div
                          className="tid-create-driver__truck
                          create-driver__two-column-item"
                        >
                          <TwAutocomplete
                            id="truck"
                            name="truck"
                            label="Truck"
                            optionList={trucks}
                            value={values.defaultTruck}
                            onChange={(
                              _,
                              newValue: TwAutocompleteOption | null
                            ): Promise<void> | Promise<FormikErrors<CreateDriverFormValues>> => {
                              return setFieldValue('defaultTruck', newValue);
                            }}
                            listAction={{
                              label: 'New truck',
                              action: (): void => {
                                setCreateEquipmentType(EquipmentType.truck);
                                setCreateEquipmentDialogOpen(true);
                              }
                            }}
                          />
                        </div>

                        <div
                          className="tid-create-driver__trailer
                          create-driver__two-column-item"
                        >
                          <TwAutocomplete
                            id="trailer"
                            name="trailer"
                            label="Trailer"
                            optionList={trailers}
                            value={values.defaultTrailer}
                            onChange={(
                              _,
                              newValue: TwAutocompleteOption | null
                            ): Promise<void> | Promise<FormikErrors<CreateDriverFormValues>> => {
                              return setFieldValue('defaultTrailer', newValue);
                            }}
                            listAction={{
                              label: 'New trailer',
                              action: (): void => {
                                setCreateEquipmentType(EquipmentType.trailer);
                                setCreateEquipmentDialogOpen(true);
                              }
                            }}
                          />
                        </div>
                      </div>

                      <label className="create-driver__sub-title">Settlement info</label>

                      <div className="create-driver__two-column">
                        <div
                          className="tid-create-driver__type
                          create-driver__two-column-item"
                        >
                          <TwAutocomplete
                            id="type"
                            name="type"
                            label="Type"
                            optionList={types}
                            value={values.type}
                            onChange={(
                              _,
                              newValue: TwAutocompleteOption | null
                            ): Promise<void> | Promise<FormikErrors<CreateDriverFormValues>> => {
                              return setFieldValue('type', newValue);
                            }}
                          />
                        </div>

                        {userData?.tenantSettings && userData.tenantSettings['multiple-carriers'] && (
                          <div
                            className="tid-create-driver__carrier
                          create-driver__two-column-item"
                          >
                            <TwAutocomplete
                              id="carrier"
                              name="carrier"
                              label="Carrier"
                              optionList={carriers}
                              value={values.carrier}
                              onChange={(
                                _,
                                newValue: TwAutocompleteOption | null
                              ): Promise<void> | Promise<FormikErrors<CreateDriverFormValues>> => {
                                return setFieldValue('carrier', newValue);
                              }}
                              listAction={{
                                label: 'New carrier',
                                action: (): void => setCreateCarrierDialogOpen(true)
                              }}
                            />
                          </div>
                        )}
                      </div>

                      <div className="create-driver__two-column">
                        <div
                          className="tid-create-driver__pay-rate-type
                          create-driver__two-column-item"
                        >
                          <TwDriverPayRateTypeSelector
                            id="pay-rate-type"
                            name="pay-rate-type"
                            label="Pay rate type"
                            value={values.defaultPayRateType}
                            onChange={(
                              _,
                              newValue: TwAutocompleteOption | null
                            ): Promise<void> | Promise<FormikErrors<CreateDriverFormValues>> => {
                              setSelectedDefaultPayRateType(
                                newValue ? (newValue.id as DRIVER_DEFAULT_PAY_RATE_TYPE) : null
                              );
                              return setFieldValue('defaultPayRateType', newValue);
                            }}
                          />
                        </div>
                        <div
                          className="tid-create-driver__rate
                          create-driver__two-column-item"
                        >
                          <TwField
                            id="payRate"
                            name="payRate"
                            value={values.payRate}
                            label="Rate"
                            inputType={TwInputTypes.number}
                            onInputChange={handleChange}
                            error={false}
                            startAdorment={
                              selectedDefaultPayRateType &&
                              selectedDefaultPayRateType !== DRIVER_DEFAULT_PAY_RATE_TYPE.PERCENTAGE
                                ? '$'
                                : undefined
                            }
                            endAdorment={
                              selectedDefaultPayRateType &&
                              selectedDefaultPayRateType == DRIVER_DEFAULT_PAY_RATE_TYPE.PERCENTAGE
                                ? '%'
                                : undefined
                            }
                          />
                        </div>
                      </div>

                      {enableFuelTransactions && (
                        <TwCheckbox
                          id="deductFuelPurchases"
                          name="deductFuelPurchases"
                          label="Deduct fuel purchases on driver settlements"
                          value={values.deductFuelPurchases}
                          checked={values.deductFuelPurchases}
                          onInputChange={handleChange}
                        />
                      )}
                    </>
                  )}
                </div>
                <div style={{ display: eldFormExpanded && showELDForm ? 'block' : 'none' }}>
                  {showELDForm && (
                    <>
                      <label className="create-driver__sub-title">ELD Connection</label>

                      <div className="create-driver__two-column">
                        <div
                          className="tid-create-driver__eld-connection
                          create-driver__two-column-item"
                        >
                          <TwAutocomplete
                            id="eldConnectionId"
                            name="eldConnectionId"
                            label="ELD Integration"
                            optionList={connectionsOptions}
                            value={values.eldConnectionId}
                            onChange={(_, newValue: TwAutocompleteOption | null): void => {
                              setFieldValue('eldConnectionId', newValue);
                              setFieldValue('eldData', null);
                              setSelectedEldIntegration(
                                newValue ? (newValue.value as string) : null
                              );
                            }}
                          />
                        </div>

                        <div
                          className="tid-create-driver__eld-data
                          create-driver__two-column-item"
                        >
                          <TwAutocomplete
                            id="eldData"
                            name="eldData"
                            label="ELD connected driver"
                            optionList={eldDriversOptions}
                            value={values.eldData}
                            onChange={(_, newValue: TwAutocompleteOption | null): void => {
                              setFieldValue('eldData', newValue);
                            }}
                          />
                        </div>
                      </div>
                    </>
                  )}
                </div>
                <div key="0" style={addEquipmentStyle} onClick={(): void => setFormExpanded(true)}>
                  + Add equipment and pay info
                </div>{' '}
                {showELDForm && (
                  <div key="1" style={addEldStyle} onClick={(): void => setEldFormExpanded(true)}>
                    + Add ELD connection
                  </div>
                )}
              </div>
            </div>
          </div>
        </TwDialog>
      </Box>

      <TwCompaniesModal
        open={createCarrierDialogOpen}
        onClose={(): void => setCreateCarrierDialogOpen(false)}
        onSuccess={handleCarrierCreated}
        entity={CompaniesEntities.carrier}
      />
      <CreateEquipment
        open={createEquipmentDialogOpen}
        defaultType={createEquipmentType}
        onClose={(): void => setCreateEquipmentDialogOpen(false)}
        onSuccess={handleEquipmentCreated}
      />
    </>
  );
};

export default CreateDriver;
